<script setup>
import AiUsageReceipt from 'dashboard/components-next/ai/AiUsageReceipt.vue';
import AiIdentity from 'dashboard/components-next/ai/AiIdentity.vue';
import { AI_NAME } from 'dashboard/helper/aiIdentity';
import { useRoute } from 'vue-router';
import BankLogo from '../settings/integrations/BankLogo.vue';
/* eslint-disable no-alert, no-use-before-define */
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useAlert } from 'dashboard/composables';
import TalkviaCreditDashboardAPI from 'dashboard/api/talkviaCreditDashboard';
import NextButton from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import WootDatePicker from 'dashboard/components/ui/DatePicker/DatePicker.vue';
import { DATE_RANGE_TYPES } from 'dashboard/components/ui/DatePicker/helpers/DatePickerHelper';

import CpfReveal from './CpfReveal.vue';
import RadarModal from './RadarModal.vue';
import ConversionMetrics from './ConversionMetrics.vue';

const route = useRoute();
const aiResult = ref(null);
const aiBusy = ref(false);
const aiError = ref('');
let analysisVersion = 0;
const analyze = async () => {
  if (aiBusy.value) return;
  analysisVersion += 1;
  const version = analysisVersion;
  aiBusy.value = true;
  aiError.value = '';
  aiResult.value = null;
  try {
    const response = await TalkviaCreditDashboardAPI.analyze(periodParams());
    if (version === analysisVersion) aiResult.value = response.data;
  } catch (error) {
    if (version === analysisVersion && error.response?.data?.usage) {
      aiResult.value = { analysis: '', usage: error.response.data.usage };
    }
    if (version === analysisVersion)
      aiError.value =
        error.response?.data?.error || 'Não foi possível gerar a análise.';
  } finally {
    if (version === analysisVersion) aiBusy.value = false;
  }
};

const radarFilter = ref(null);

const copy = {
  title: 'Pulso CLT',
  description:
    'Uma leitura viva da sua operação: oportunidade, conversão, produção e saúde bancária em um só lugar.',
  updatedNow: 'Atualizado agora',
  autoRefresh: 'Tempo real',
  refresh: 'Atualizar',
  loading: 'Carregando indicadores...',
  empty: 'Sem dados no período selecionado.',
  failed: 'Não foi possível carregar o dashboard de crédito.',
  funnel: 'Esteira de conversão',
  trend: 'Ritmo da produção',
  banks: 'Performance por banco',
  bankStages: 'Jornada por banco',
  status: 'Radar operacional',
  activity: 'Fluxo recente',
  suspicious: 'Simulações suspeitas',
  suspiciousHelp:
    'Valores fora do limite operacional foram removidos dos totais. Revise e exclua quando for erro de retorno.',
};

const dateRange = ref([new Date(), new Date()]);
watch(
  [
    () => route.params.accountId,
    () => dateRange.value.map(value => value?.toISOString()).join('/'),
  ],
  () => {
    analysisVersion += 1;
    aiResult.value = null;
    aiError.value = '';
    aiBusy.value = false;
  }
);
const rangeType = ref(DATE_RANGE_TYPES.TODAY);
const dashboard = ref(null);
const isLoading = ref(false);
const isRefreshing = ref(false);
const isExportingJourney = ref(false);
const selectedExportStage = ref('all');
const maskExportCpf = ref(false);
const deletingSimulationId = ref(null);
const autoRefresh = ref(true);
const lastUpdatedAt = ref(null);
let refreshTimer = null;

const dashboardData = computed(() => dashboard.value || {});
const totals = computed(() => dashboardData.value.totals || {});
const funnel = computed(() => dashboardData.value.funnel || []);
const dailySeries = computed(() => dashboardData.value.daily_series || []);
const bankIntegrations = computed(
  () => dashboardData.value.bank_integrations || []
);
const bankStageMetrics = computed(
  () => dashboardData.value.bank_stage_metrics || []
);
const statusMix = computed(() => dashboardData.value.status_mix || {});
const recentActivity = computed(
  () => dashboardData.value.recent_activity || []
);
const suspiciousSimulations = computed(
  () => dashboardData.value.suspicious_simulations || []
);

const journeyStages = [
  { key: 'no_offer', label: 'Sem oferta', color: 'bg-n-ruby-9' },
  {
    key: 'authorization',
    label: 'Coleta de autorização',
    color: 'bg-n-blue-9',
  },
  { key: 'offer', label: 'Cliente com oferta', color: 'bg-n-teal-9' },
  { key: 'paid', label: 'Pago', color: 'bg-n-grass-9' },
];

const journeyExportOptions = [
  { value: 'all', label: 'Toda a jornada' },
  ...journeyStages.map(stage => ({ value: stage.key, label: stage.label })),
];

const hasData = computed(
  () =>
    totals.value.leads_count ||
    totals.value.simulations_count ||
    totals.value.proposals_count ||
    totals.value.paid_count
);

// Destaques: o valor produzido em primeiro plano, depois leads e total ofertado.
const paidHighlight = computed(() => ({
  label: 'Total pago',
  isZero: !Number(totals.value.paid_amount),
  value: formatCurrency(totals.value.paid_amount),
  caption: `${formatNumber(totals.value.paid_count)} pagamentos · ${formatNumber(totals.value.completed_contracts_count)} contratos concluídos`,
  details: [
    {
      label: 'Total digitado',
      value: formatCurrency(totals.value.typed_amount),
      caption: `${formatNumber(totals.value.proposals_count)} propostas`,
      icon: 'i-lucide-keyboard',
    },
    {
      label: 'Total perdido',
      value: formatCurrency(totals.value.lost_amount),
      caption: `${formatNumber(totals.value.lost_count)} perdas`,
      icon: 'i-lucide-circle-x',
    },
  ],
}));

const highlightCards = computed(() => [
  {
    label: 'Total de leads',
    isZero: !Number(totals.value.leads_count),
    value: formatNumber(totals.value.leads_count),
    caption: 'Leads no período selecionado',
    icon: 'i-hugeicons-user-group',
    tone: 'blue',
  },
  {
    label: 'Total ofertado',
    isZero: !Number(totals.value.offered_amount),
    value: formatCurrency(totals.value.offered_amount),
    caption: `${formatNumber(totals.value.simulations_with_balance_count)} CPFs distintos com oferta. Maior oferta por CPF na última rodada com retorno válido, sem somar bancos.`,
    icon: 'i-lucide-badge-dollar-sign',
    tone: 'brand',
  },
]);

const analysisCards = computed(() => [
  {
    label: 'Taxa de oferta',
    isZero:
      !Number(totals.value.simulations_count) ||
      !Number(totals.value.simulations_with_balance_count),
    value: formatPercent(
      totals.value.simulations_count
        ? (totals.value.simulations_with_balance_count /
            totals.value.simulations_count) *
            100
        : 0
    ),
    caption: `${formatNumber(totals.value.simulations_with_balance_count)} CPFs distintos com oferta / ${formatNumber(totals.value.simulations_count)} CPFs distintos simulados`,
    icon: 'i-lucide-chart-no-axes-combined',
    tone: 'blue',
  },
  {
    label: 'Conversão lead → pago',
    isZero: !Number(totals.value.lead_to_paid_conversion),
    value: formatPercent(totals.value.lead_to_paid_conversion),
    caption: `${formatNumber(totals.value.paid_count)} pagos de ${formatNumber(totals.value.leads_count)} leads`,
    icon: 'i-lucide-route',
    tone: 'amber',
  },
  {
    label: 'Ticket médio pago',
    isZero:
      !Number(totals.value.paid_count) || !Number(totals.value.paid_amount),
    value: formatCurrency(
      totals.value.paid_count
        ? totals.value.paid_amount / totals.value.paid_count
        : 0
    ),
    caption: 'Valor pago por pessoa, sem duplicar contratos',
    icon: 'i-lucide-badge-dollar-sign',
    tone: 'green',
  },
  {
    label: 'Produção por lead',
    isZero: !Number(totals.value.production_per_lead),
    value: formatCurrency(totals.value.production_per_lead),
    caption: 'Base: total pago/concluído',
    icon: 'i-lucide-user-round-check',
    tone: 'slate',
  },
]);

const trendMax = computed(() => {
  const values = dailySeries.value.flatMap(item => [
    Number(item.offered_amount || 0),
    Number(item.typed_amount || 0),
    Number(item.paid_amount || 0),
  ]);
  return Math.max(...values, 1);
});

const chartPoints = key => {
  const series = dailySeries.value;
  if (series.length === 0) return '';
  if (series.length === 1) {
    const y = 120 - (Number(series[0][key] || 0) / trendMax.value) * 96;
    return `0,${y} 640,${y}`;
  }
  return series
    .map((item, index) => {
      const x = (index / (series.length - 1)) * 640;
      const y = 120 - (Number(item[key] || 0) / trendMax.value) * 96;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
};

const chartTotal = key =>
  dailySeries.value.reduce((sum, item) => sum + Number(item[key] || 0), 0);

const topBanks = computed(() => bankIntegrations.value.slice(0, 6));
const bankMaxAmount = computed(() =>
  Math.max(...bankIntegrations.value.map(item => Number(item.amount || 0)), 1)
);
const bankStageMax = computed(() =>
  Math.max(
    ...bankStageMetrics.value.flatMap(item => [
      Number(item.no_offer || 0),
      Number(item.authorization || 0),
      Number(item.offer || 0),
      Number(item.paid || 0),
    ]),
    1
  )
);

const statusBlocks = computed(() => [
  {
    label: 'Propostas',
    source: 'proposal',
    items: statusEntries(statusMix.value.proposals),
  },
  {
    label: 'Contratos',
    source: 'contract',
    items: statusEntries(statusMix.value.contracts),
  },
  {
    label: 'Simulações',
    source: 'simulation',
    items: statusEntries(statusMix.value.simulations),
  },
]);

const loadDashboard = async ({ silent = false } = {}) => {
  const [startDate, endDate] = dateRange.value;
  if (!startDate || !endDate) return;

  if (silent) {
    isRefreshing.value = true;
  } else {
    isLoading.value = true;
  }
  try {
    const { data } = await TalkviaCreditDashboardAPI.get(periodParams());
    dashboard.value = data;
    lastUpdatedAt.value = new Date();
  } catch {
    useAlert(copy.failed);
  } finally {
    isLoading.value = false;
    isRefreshing.value = false;
  }
};

const removeSuspiciousSimulation = async item => {
  if (!item?.id) return;
  const confirmed = window.confirm(
    `Excluir a simulação ${item.provider_label || item.provider} do lote ${item.batch_id}?`
  );
  if (!confirmed) return;

  deletingSimulationId.value = item.id;
  try {
    await TalkviaCreditDashboardAPI.deleteSimulationResult(item.id);
    useAlert('Simulação excluída.');
    await loadDashboard({ silent: true });
  } catch {
    useAlert('Não foi possível excluir a simulação.');
  } finally {
    deletingSimulationId.value = null;
  }
};

const exportJourney = async () => {
  isExportingJourney.value = true;
  try {
    const response = await TalkviaCreditDashboardAPI.exportJourney({
      ...periodParams(),
      stage: selectedExportStage.value,
      mask_cpf: maskExportCpf.value,
    });
    const disposition = response.headers?.['content-disposition'] || '';
    const encodedFilename = disposition.match(
      /filename\*=UTF-8''([^;]+)/i
    )?.[1];
    const regularFilename = disposition.match(/filename="?([^";]+)"?/i)?.[1];
    const filename = encodedFilename
      ? decodeURIComponent(encodedFilename)
      : regularFilename || 'pulso-clt-jornada.xlsx';
    const url = window.URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch {
    useAlert('Não foi possível exportar a jornada por banco.');
  } finally {
    isExportingJourney.value = false;
  }
};

const setupAutoRefresh = () => {
  clearInterval(refreshTimer);
  refreshTimer = null;
  if (!autoRefresh.value) return;
  refreshTimer = setInterval(() => loadDashboard({ silent: true }), 15000);
};

const onDateRangeChanged = ([startDate, endDate, selectedRange]) => {
  if (!startDate || !endDate) return;

  radarFilter.value = null;
  dateRange.value = [startDate, endDate];
  rangeType.value = selectedRange;
  loadDashboard();
};

const periodParams = () => {
  const [startDate, endDate] = dateRange.value;
  return {
    period: rangeType.value,
    date_start: formatDateInput(startDate),
    date_end: formatDateInput(endDate),
  };
};

function formatCurrency(value) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  }).format(Number(value || 0));
}

function formatNumber(value) {
  return new Intl.NumberFormat('pt-BR').format(Number(value || 0));
}

function formatPercent(value) {
  return `${Number(value || 0).toLocaleString('pt-BR', {
    maximumFractionDigits: 1,
  })}%`;
}

function formatDate(value) {
  if (!value) return '';
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: 'short',
  }).format(new Date(value));
}

function formatTime(value) {
  if (!value) return '';
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
}

function formatDateInput(value) {
  const timezoneOffset = value.getTimezoneOffset() * 60 * 1000;
  return new Date(value.getTime() - timezoneOffset).toISOString().slice(0, 10);
}

function barWidth(value, max = 100) {
  if (!max) return '0%';
  return `${Math.min((Number(value || 0) / max) * 100, 100)}%`;
}

function toneClass(tone) {
  return {
    brand: 'bg-n-brand/10 text-n-brand',
    blue: 'bg-n-blue-3 text-n-blue-11',
    green: 'bg-n-grass-3 text-n-grass-11',
    amber: 'bg-n-amber-3 text-n-amber-11',
    ruby: 'bg-n-ruby-3 text-n-ruby-11',
    slate: 'bg-n-slate-3 text-n-slate-11',
  }[tone];
}

function statusEntries(payload = {}) {
  return Object.entries(payload)
    .filter(([, value]) => Number(value || 0) > 0)
    .map(([key, value]) => ({
      key,
      label: key.replaceAll('_', ' '),
      value,
    }));
}

function activityIcon(type) {
  return {
    simulation: 'i-lucide-landmark',
    proposal: 'i-lucide-file-pen-line',
    contract: 'i-lucide-file-signature',
  }[type];
}

watch(autoRefresh, setupAutoRefresh);

onMounted(() => {
  loadDashboard();
  setupAutoRefresh();
});

onBeforeUnmount(() => clearInterval(refreshTimer));
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <main
    class="clt-pulse flex h-full min-h-0 flex-col overflow-x-hidden overflow-y-auto bg-n-background"
  >
    <header class="clt-pulse__hero relative shrink-0 px-6 py-7 lg:px-8">
      <div
        aria-hidden="true"
        class="clt-pulse__orbit clt-pulse__orbit--outer pointer-events-none absolute -right-24 -top-40 size-[30rem] rounded-full border border-white/10"
      >
        <span class="clt-pulse__orbit-dot" />
      </div>
      <div
        aria-hidden="true"
        class="clt-pulse__orbit clt-pulse__orbit--inner pointer-events-none absolute -right-4 -top-20 size-72 rounded-full border border-n-brand/20"
      >
        <span class="clt-pulse__orbit-dot" />
      </div>
      <div
        class="pointer-events-none absolute bottom-0 left-1/3 h-px w-1/2 bg-gradient-to-r from-transparent via-n-brand/50 to-transparent"
      />

      <div class="relative">
        <div
          class="flex flex-col gap-6 xl:flex-row xl:items-start xl:justify-between"
        >
          <div class="flex min-w-0 items-start gap-4">
            <span
              class="grid size-12 shrink-0 place-items-center rounded-2xl bg-n-brand text-white shadow-lg shadow-n-brand/20"
            >
              <span class="i-lucide-activity size-6" />
            </span>
            <div class="min-w-0">
              <div
                class="mb-2 flex items-center gap-2 text-[0.68rem] font-semibold uppercase tracking-[0.22em] text-[#8ec5ff]"
              >
                <span>Talkvia Intelligence</span>
                <span class="size-1 rounded-full bg-n-brand" />
                <span>Operação CLT</span>
              </div>
              <h1
                class="mb-1 text-3xl font-semibold tracking-[-0.04em] text-white lg:text-4xl"
              >
                {{ copy.title }}
              </h1>
              <p class="mb-0 max-w-2xl text-sm leading-6 text-white/60">
                {{ copy.description }}
              </p>
            </div>
          </div>

          <div class="flex flex-wrap items-center gap-2">
            <div
              v-if="lastUpdatedAt"
              class="flex h-10 items-center gap-2 rounded-full border border-white/10 bg-white/[0.06] px-4 text-xs font-medium text-white/70 backdrop-blur"
            >
              <span class="relative flex size-2">
                <span
                  class="absolute inline-flex size-full animate-ping rounded-full bg-n-brand opacity-50"
                />
                <span
                  class="relative inline-flex size-2 rounded-full bg-n-brand"
                />
              </span>
              {{ copy.updatedNow }} · {{ formatTime(lastUpdatedAt) }}
            </div>
            <button
              type="button"
              class="flex h-10 items-center gap-2 rounded-full border border-white/10 bg-white/[0.06] px-4 text-sm font-medium text-white transition hover:bg-white/10"
              @click="loadDashboard({ silent: true })"
            >
              <span
                class="size-4"
                :class="
                  isRefreshing
                    ? 'i-lucide-loader-2 animate-spin'
                    : 'i-lucide-refresh-cw'
                "
              />
              {{ copy.refresh }}
            </button>
          </div>
        </div>

        <div
          class="mt-7 flex flex-col gap-4 border-t border-white/10 pt-5 xl:flex-row xl:items-end xl:justify-between"
        >
          <div class="flex min-w-0 flex-col gap-1.5">
            <span class="text-xs font-medium text-white/80">Período</span>
            <div class="clt-pulse__date-picker">
              <WootDatePicker
                v-model:date-range="dateRange"
                v-model:range-type="rangeType"
                @date-range-changed="onDateRangeChanged"
              />
            </div>
          </div>

          <label
            class="flex h-10 w-fit items-center gap-3 rounded-full border border-white/10 px-4 text-xs font-medium text-white/65"
          >
            <input
              v-model="autoRefresh"
              type="checkbox"
              class="rounded border-white/20 bg-transparent text-n-brand"
            />
            Atualização automática a cada 15s
          </label>
        </div>

        <div class="mt-6 grid gap-3 md:grid-cols-3">
          <div class="rounded-2xl border border-white/10 bg-white/[0.055] p-4">
            <p
              class="mb-2 text-xs font-medium uppercase tracking-wider text-white/45"
            >
              Produção paga
            </p>
            <strong class="text-2xl font-semibold tracking-tight text-white">
              {{ formatCurrency(totals.paid_amount) }}
            </strong>
          </div>
          <div class="rounded-2xl border border-white/10 bg-white/[0.055] p-4">
            <p
              class="mb-2 text-xs font-medium uppercase tracking-wider text-white/45"
            >
              Conversão ponta a ponta
            </p>
            <strong
              class="text-2xl font-semibold tracking-tight text-[#8ec5ff]"
            >
              {{ formatPercent(totals.lead_to_paid_conversion) }}
            </strong>
          </div>
          <div class="rounded-2xl border border-white/10 bg-white/[0.055] p-4">
            <p
              class="mb-2 text-xs font-medium uppercase tracking-wider text-white/45"
            >
              Bancos no radar
            </p>
            <strong class="text-2xl font-semibold tracking-tight text-white">
              {{ formatNumber(bankIntegrations.length) }}
            </strong>
          </div>
        </div>
      </div>
    </header>
    <section
      class="mx-6 my-5 rounded-xl border border-n-weak bg-n-solid-2 p-5 space-y-4"
      aria-label="Análise com Lia"
    >
      <div class="flex items-center justify-between flex-wrap gap-4">
        <div class="flex items-center gap-3">
          <AiIdentity />
          <div>
            <h2 class="m-0 text-base font-semibold">
              {{ AI_NAME }} no Pulso CLT
            </h2>
            <p class="m-0 text-sm text-n-slate-11">
              Analise os indicadores do período e veja as próximas ações
              sugeridas.
            </p>
          </div>
        </div>
        <NextButton
          :label="`Analisar com ${AI_NAME}`"
          :is-loading="aiBusy"
          :disabled="aiBusy || !dashboard"
          @click="analyze"
        />
      </div>
      <p class="text-xs text-n-slate-11 m-0">
        Ao analisar, os indicadores agregados deste período serão processados
        pela OpenAI. O consumo aparecerá abaixo.
      </p>
      <p v-if="aiError" role="alert" class="text-sm text-n-ruby-11">
        {{ aiError }}
      </p>
      <template v-if="aiResult">
        <p class="whitespace-pre-wrap text-sm text-n-slate-12">
          {{ aiResult.analysis }}
        </p>
        <AiUsageReceipt :usage="aiResult.usage" />
      </template>
    </section>

    <section v-if="isLoading" class="grid flex-1 place-items-center p-8">
      <div class="flex items-center gap-2 text-sm text-n-slate-11">
        <span class="i-lucide-loader-2 size-4 animate-spin" />
        {{ copy.loading }}
      </div>
    </section>

    <section
      v-else
      class="clt-pulse__content flex shrink-0 flex-col gap-6 p-5 lg:p-8"
    >
      <div class="grid gap-4 lg:grid-cols-2 xl:grid-cols-4">
        <article
          class="clt-pulse__metric relative overflow-hidden rounded-2xl border border-n-brand/25 bg-n-brand/5 p-6 lg:col-span-2 xl:col-span-2"
        >
          <div class="flex items-start justify-between gap-3">
            <div class="min-w-0">
              <p
                class="mb-2 text-xs font-semibold uppercase tracking-[0.12em] text-n-slate-11"
              >
                {{ paidHighlight.label }}
              </p>
              <strong
                class="block truncate text-4xl font-semibold tracking-[-0.03em] lg:text-5xl"
                :class="
                  paidHighlight.isZero ? 'text-n-slate-11' : 'text-n-slate-12'
                "
              >
                {{ paidHighlight.value }}
              </strong>
              <p class="mb-0 mt-2 text-sm text-n-slate-11">
                {{ paidHighlight.caption }}
              </p>
            </div>
            <span
              class="grid size-11 shrink-0 place-items-center rounded-xl bg-n-brand text-white"
            >
              <span class="i-lucide-circle-check-big size-5" />
            </span>
          </div>
          <dl
            class="mb-0 mt-5 grid gap-3 border-t border-n-brand/15 pt-4 sm:grid-cols-2"
          >
            <div
              v-for="detail in paidHighlight.details"
              :key="detail.label"
              class="flex min-w-0 items-center gap-3"
            >
              <span
                class="grid size-9 shrink-0 place-items-center rounded-lg bg-n-alpha-2 text-n-slate-11"
              >
                <span :class="detail.icon" class="size-4" />
              </span>
              <div class="min-w-0">
                <dt class="text-xs text-n-slate-11">{{ detail.label }}</dt>
                <dd
                  class="m-0 truncate text-base font-semibold text-n-slate-12"
                >
                  {{ detail.value }}
                  <span class="ml-1 text-xs font-normal text-n-slate-10">
                    {{ detail.caption }}
                  </span>
                </dd>
              </div>
            </div>
          </dl>
        </article>
        <article
          v-for="metric in highlightCards"
          :key="metric.label"
          class="clt-pulse__metric flex flex-col rounded-2xl border border-n-weak bg-n-solid-1 p-6"
        >
          <div class="flex items-start justify-between gap-3">
            <p
              class="mb-2 text-xs font-semibold uppercase tracking-[0.12em] text-n-slate-10"
            >
              {{ metric.label }}
            </p>
            <span
              class="grid size-10 shrink-0 place-items-center rounded-xl"
              :class="toneClass(metric.tone)"
            >
              <span class="size-5" :class="metric.icon" />
            </span>
          </div>
          <strong
            class="block truncate text-3xl font-semibold tracking-[-0.03em] lg:text-4xl"
            :class="metric.isZero ? 'text-n-slate-11' : 'text-n-slate-12'"
          >
            {{ metric.value }}
          </strong>
          <p class="mb-0 mt-auto pt-3 text-sm text-n-slate-11">
            {{ metric.caption }}
          </p>
        </article>
      </div>

      <section aria-labelledby="clt-pulse-analysis">
        <h2
          id="clt-pulse-analysis"
          class="mb-3 text-xs font-semibold uppercase tracking-[0.12em] text-n-slate-10"
        >
          Análises
        </h2>
        <div class="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <article
            v-for="metric in analysisCards"
            :key="metric.label"
            class="clt-pulse__metric flex items-start gap-3 rounded-xl border border-n-weak bg-n-solid-1 p-4"
          >
            <span
              class="grid size-9 shrink-0 place-items-center rounded-lg"
              :class="toneClass(metric.tone)"
            >
              <span class="size-4" :class="metric.icon" />
            </span>
            <div class="min-w-0">
              <p class="mb-1 text-xs font-medium text-n-slate-11">
                {{ metric.label }}
              </p>
              <strong
                class="block truncate text-xl font-semibold"
                :class="metric.isZero ? 'text-n-slate-11' : 'text-n-slate-12'"
              >
                {{ metric.value }}
              </strong>
              <p class="mb-0 mt-1 text-xs leading-5 text-n-slate-10">
                {{ metric.caption }}
              </p>
            </div>
          </article>
        </div>
      </section>

      <ConversionMetrics :data="dashboardData.conversion_attribution || {}" />

      <div
        v-if="!hasData"
        class="rounded-2xl border border-dashed border-n-weak bg-n-solid-1 p-10 text-center"
      >
        <span
          class="i-lucide-chart-spline mx-auto mb-3 block size-8 text-n-slate-10"
        />
        <p class="mb-0 text-sm text-n-slate-11">{{ copy.empty }}</p>
      </div>

      <div
        class="grid gap-5 xl:grid-cols-[minmax(0,1.1fr)_minmax(22rem,0.9fr)]"
      >
        <section
          class="clt-pulse__panel rounded-[1.5rem] border border-n-weak bg-n-solid-1 p-5"
        >
          <div class="mb-4 flex items-center justify-between gap-3">
            <div>
              <h2 class="mb-0 text-base font-semibold text-n-slate-12">
                {{ copy.trend }}
              </h2>
              <p class="mb-0 text-sm text-n-slate-11">
                Ofertado, digitado e pago no período
              </p>
            </div>
            <div
              class="hidden items-center gap-3 text-xs text-n-slate-10 sm:flex"
            >
              <span class="flex items-center gap-1">
                <i class="size-2 rounded-full bg-n-blue-9" />
                Ofertado
              </span>
              <span class="flex items-center gap-1">
                <i class="size-2 rounded-full bg-n-amber-9" />
                Digitado
              </span>
              <span class="flex items-center gap-1">
                <i class="size-2 rounded-full bg-n-teal-9" />
                Pago
              </span>
            </div>
          </div>

          <svg
            class="h-64 w-full overflow-visible"
            viewBox="0 0 640 140"
            role="img"
          >
            <line
              v-for="line in [24, 48, 72, 96, 120]"
              :key="line"
              x1="0"
              x2="640"
              :y1="line"
              :y2="line"
              class="stroke-n-weak"
              stroke-width="1"
            />
            <polyline
              :points="chartPoints('offered_amount')"
              fill="none"
              class="stroke-n-blue-9"
              stroke-width="4"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <polyline
              :points="chartPoints('typed_amount')"
              fill="none"
              class="stroke-n-amber-9"
              stroke-width="4"
              stroke-dasharray="9 6"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <polyline
              :points="chartPoints('paid_amount')"
              fill="none"
              class="stroke-n-teal-9"
              stroke-width="4"
              stroke-dasharray="2 6"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>

          <div class="grid gap-3 border-t border-n-weak pt-4 sm:grid-cols-3">
            <div>
              <span class="text-xs text-n-slate-10">Ofertado</span>
              <p class="mb-0 text-sm font-semibold text-n-slate-12">
                {{ formatCurrency(chartTotal('offered_amount')) }}
              </p>
            </div>
            <div>
              <span class="text-xs text-n-slate-10">Digitado</span>
              <p class="mb-0 text-sm font-semibold text-n-slate-12">
                {{ formatCurrency(chartTotal('typed_amount')) }}
              </p>
            </div>
            <div>
              <span class="text-xs text-n-slate-10">Pago</span>
              <p class="mb-0 text-sm font-semibold text-n-slate-12">
                {{ formatCurrency(chartTotal('paid_amount')) }}
              </p>
            </div>
          </div>
        </section>

        <section
          class="clt-pulse__panel rounded-[1.5rem] border border-n-weak bg-n-solid-1 p-5"
        >
          <h2 class="mb-1 text-base font-semibold text-n-slate-12">
            {{ copy.funnel }}
          </h2>
          <p class="mb-4 text-sm text-n-slate-11">
            Conversão por etapa comparada à média operacional
          </p>

          <div class="flex flex-col gap-4">
            <article v-for="stage in funnel" :key="stage.key">
              <div class="mb-2 flex items-center justify-between gap-3">
                <div class="min-w-0">
                  <div class="flex items-center gap-2">
                    <h3
                      class="mb-0 truncate text-sm font-semibold text-n-slate-12"
                    >
                      {{ stage.label }}
                    </h3>
                    <span
                      class="rounded-full bg-n-alpha-2 px-2 py-0.5 text-xs font-medium text-n-slate-11"
                    >
                      {{ formatNumber(stage.count) }}
                    </span>
                  </div>
                </div>
                <span class="shrink-0 text-sm text-n-slate-10">
                  {{ formatPercent(stage.top_percentage) }} do topo
                </span>
              </div>
              <div class="h-2 rounded-full bg-n-alpha-2">
                <div
                  class="h-2 rounded-full bg-n-brand"
                  :style="{ width: barWidth(stage.top_percentage) }"
                />
              </div>
              <div class="mt-2 flex flex-wrap items-center gap-2 text-xs">
                <span class="text-n-slate-11">
                  Conv. etapa:
                  <strong class="text-n-slate-12">
                    {{ formatPercent(stage.conversion) }}
                  </strong>
                </span>
                <span class="text-n-slate-10">
                  Média: {{ formatPercent(stage.benchmark) }}
                </span>
                <span
                  class="rounded-md px-2 py-1 font-medium"
                  :class="
                    stage.status === 'above'
                      ? 'bg-n-grass-3 text-n-grass-11'
                      : 'bg-n-ruby-3 text-n-ruby-11'
                  "
                >
                  {{
                    stage.status === 'above'
                      ? 'Acima da média'
                      : 'Abaixo da média'
                  }}
                </span>
                <span
                  :class="
                    stage.delta >= 0 ? 'text-n-grass-11' : 'text-n-ruby-11'
                  "
                >
                  {{ stage.delta > 0 ? '+' : '' }}{{ stage.delta }}pp
                </span>
              </div>
            </article>
          </div>
        </section>
      </div>

      <section
        class="clt-pulse__panel rounded-[1.5rem] border border-n-weak bg-n-solid-1 p-5"
      >
        <div
          class="mb-5 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between"
        >
          <div>
            <h2 class="mb-0 text-base font-semibold text-n-slate-12">
              {{ copy.bankStages }}
            </h2>
            <p class="mb-0 text-sm text-n-slate-11">
              Da consulta ao pagamento, com CPFs únicos por banco integrado
            </p>
          </div>
          <div
            class="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center"
          >
            <div class="w-full sm:w-56">
              <ComboBox
                v-model="selectedExportStage"
                :options="journeyExportOptions"
                placeholder="Toda a jornada"
              />
            </div>
            <label
              class="flex h-10 shrink-0 cursor-pointer items-center gap-2 rounded-lg px-2 text-sm text-n-slate-11"
            >
              <Checkbox v-model="maskExportCpf" />
              <span>Mascarar CPF</span>
            </label>
            <NextButton
              label="Exportar jornada"
              icon="i-lucide-file-spreadsheet"
              class="w-full shrink-0 sm:w-auto"
              :disabled="isExportingJourney"
              :is-loading="isExportingJourney"
              @click="exportJourney"
            />
          </div>
        </div>

        <div class="mb-4 flex flex-wrap items-center gap-4 text-xs">
          <span
            v-for="stage in journeyStages"
            :key="stage.key"
            class="flex items-center gap-2 text-n-slate-11"
          >
            <i class="size-2.5 rounded-full" :class="stage.color" />
            {{ stage.label }}
          </span>
        </div>

        <div class="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          <article
            v-for="bank in bankStageMetrics"
            :key="bank.provider"
            class="rounded-2xl border border-n-weak bg-n-alpha-1 p-4"
          >
            <div class="mb-4 flex items-center justify-between gap-3">
              <h3
                class="mb-0 flex items-center gap-2 text-sm font-semibold text-n-slate-12"
              >
                <BankLogo :provider="bank.provider" />{{ bank.label }}
              </h3>
              <span
                class="rounded-full bg-n-alpha-2 px-2.5 py-1 text-xs font-medium text-n-slate-11"
              >
                {{ formatNumber(bank.cpf_count) }} CPFs
              </span>
            </div>

            <div class="space-y-3">
              <div v-for="stage in journeyStages" :key="stage.key">
                <div class="mb-1.5 flex items-center justify-between text-xs">
                  <span class="text-n-slate-11">{{ stage.label }}</span>
                  <strong class="text-n-slate-12">
                    {{ formatNumber(bank[stage.key]) }}
                  </strong>
                </div>
                <div class="h-2 rounded-full bg-n-alpha-2">
                  <div
                    class="h-2 rounded-full"
                    :class="stage.color"
                    :style="{
                      width: barWidth(bank[stage.key], bankStageMax),
                    }"
                  />
                </div>
              </div>
            </div>
            <div
              class="mt-4 border-t border-n-weak pt-3 text-xs text-n-slate-11"
            >
              <p class="mb-1">
                {{ formatNumber(bank.unclassified_count) }} CPFs com retornos
                sem classificação de oferta
              </p>
              <p class="mb-0">
                {{ formatNumber(bank.technical_errors) }} falhas ou pendências
                técnicas. Não representam recusa do banco.
              </p>
            </div>
          </article>

          <div
            v-if="!bankStageMetrics.length"
            class="rounded-2xl border border-dashed border-n-weak p-8 text-center text-sm text-n-slate-10 md:col-span-2 xl:col-span-3"
          >
            Nenhum banco ativo foi encontrado nas integrações bancárias.
          </div>
        </div>
      </section>

      <div class="grid gap-5 xl:grid-cols-[minmax(0,1fr)_minmax(20rem,0.55fr)]">
        <section
          class="clt-pulse__panel rounded-[1.5rem] border border-n-weak bg-n-solid-1 p-5"
        >
          <div class="mb-4 flex items-center justify-between">
            <div>
              <h2 class="mb-0 text-base font-semibold text-n-slate-12">
                {{ copy.banks }}
              </h2>
              <p class="mb-0 text-sm text-n-slate-11">
                Volume ofertado, sucesso e pendências por banco
              </p>
            </div>
          </div>

          <div class="overflow-x-auto">
            <table class="w-full min-w-[42rem] text-left text-sm">
              <thead class="text-xs uppercase text-n-slate-10">
                <tr class="border-b border-n-weak">
                  <th class="py-2 font-medium">Banco</th>
                  <th class="py-2 font-medium">Ofertado</th>
                  <th class="py-2 font-medium">Sucesso</th>
                  <th class="py-2 font-medium">Autorização</th>
                  <th class="py-2 font-medium">Sem margem</th>
                  <th class="py-2 font-medium">Erros</th>
                  <th class="py-2 font-medium">Suspeitos</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="bank in topBanks"
                  :key="bank.provider"
                  class="border-b border-n-weak last:border-0"
                >
                  <td class="py-3">
                    <div
                      class="flex items-center gap-2 font-medium text-n-slate-12"
                    >
                      <BankLogo :provider="bank.provider" />{{ bank.label }}
                    </div>
                    <div class="mt-1 h-1.5 rounded-full bg-n-alpha-2">
                      <div
                        class="h-1.5 rounded-full bg-n-brand"
                        :style="{ width: barWidth(bank.amount, bankMaxAmount) }"
                      />
                    </div>
                  </td>
                  <td class="py-3 font-semibold text-n-slate-12">
                    {{ formatCurrency(bank.amount) }}
                  </td>
                  <td class="py-3 text-n-slate-11">
                    {{ bank.success }} · {{ formatPercent(bank.conversion) }}
                  </td>
                  <td class="py-3 text-n-slate-11">
                    {{ bank.authorization_required }}
                  </td>
                  <td class="py-3 text-n-slate-11">
                    {{ bank.no_margin }}
                  </td>
                  <td class="py-3 text-n-slate-11">
                    {{ bank.errors }}
                  </td>
                  <td class="py-3">
                    <span
                      class="rounded-md px-2 py-1 text-xs font-medium"
                      :class="
                        bank.suspicious
                          ? 'bg-n-amber-3 text-n-amber-11'
                          : 'text-n-slate-10'
                      "
                    >
                      {{ bank.suspicious || 0 }}
                    </span>
                  </td>
                </tr>
                <tr v-if="!topBanks.length">
                  <td class="py-6 text-center text-n-slate-10" colspan="7">
                    Nenhuma integração bancária no período.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section
          class="clt-pulse__panel rounded-[1.5rem] border border-n-weak bg-n-solid-1 p-5"
        >
          <h2 class="mb-3 text-base font-semibold text-n-slate-12">
            {{ copy.status }}
          </h2>
          <div class="flex flex-col gap-4">
            <div v-for="block in statusBlocks" :key="block.label">
              <div class="mb-2 text-sm font-medium text-n-slate-12">
                {{ block.label }}
              </div>
              <div v-if="block.items.length" class="flex flex-wrap gap-2">
                <button
                  v-for="item in block.items"
                  :key="item.key"
                  type="button"
                  class="rounded-md bg-n-alpha-2 px-2 py-1 text-xs text-n-slate-11"
                  @click="
                    radarFilter = {
                      source: block.source,
                      status: item.key,
                      label: `${block.label} · ${item.label}`,
                    }
                  "
                >
                  {{ item.label }} · {{ item.value }}
                  <span class="i-lucide-eye ml-1 inline-block size-3" />
                </button>
              </div>
              <span v-else class="text-sm text-n-slate-10">Sem registros</span>
            </div>
          </div>
        </section>
      </div>

      <section
        v-if="suspiciousSimulations.length"
        class="rounded-[1.5rem] border border-n-amber-7 bg-n-amber-2 p-5"
      >
        <div
          class="mb-4 flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between"
        >
          <div>
            <h2
              class="mb-1 flex items-center gap-2 text-base font-semibold text-n-amber-12"
            >
              <span class="i-lucide-triangle-alert size-5" />
              {{ copy.suspicious }}
            </h2>
            <p class="mb-0 text-sm text-n-amber-11">
              {{ copy.suspiciousHelp }}
            </p>
          </div>
          <span
            class="rounded-md bg-n-amber-4 px-2 py-1 text-xs font-semibold text-n-amber-12"
          >
            {{ suspiciousSimulations.length }} registro(s)
          </span>
        </div>

        <div class="overflow-x-auto">
          <table class="w-full min-w-[48rem] text-left text-sm">
            <thead class="text-xs uppercase text-n-amber-11">
              <tr class="border-b border-n-amber-6">
                <th class="py-2 font-medium">Lote</th>
                <th class="py-2 font-medium">Cliente</th>
                <th class="py-2 font-medium">Banco</th>
                <th class="py-2 font-medium">Valor recebido</th>
                <th class="py-2 font-medium">Data</th>
                <th class="py-2 text-right font-medium">Ação</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="item in suspiciousSimulations"
                :key="item.id"
                class="border-b border-n-amber-5 last:border-0"
              >
                <td class="py-3 font-mono text-xs text-n-amber-12">
                  {{ item.batch_id }}
                </td>
                <td class="py-3 text-n-amber-12">
                  <CpfReveal :item="item" :period="periodParams()" />
                </td>
                <td class="py-3 text-n-amber-12">
                  <span class="flex items-center gap-2"
                    ><BankLogo :provider="item.provider" />{{
                      item.provider_label || item.provider
                    }}</span
                  >
                </td>
                <td class="py-3 font-semibold text-n-amber-12">
                  {{ formatCurrency(item.raw_amount) }}
                </td>
                <td class="py-3 text-n-amber-11">
                  {{ formatTime(item.occurred_at) }}
                </td>
                <td class="py-3 text-right">
                  <button
                    v-if="item.conversation_id"
                    type="button"
                    class="mr-2 text-xs text-n-blue-11"
                    @click="radarFilter = { item }"
                  >
                    Ver conversa
                  </button>
                  <button
                    type="button"
                    class="inline-flex h-8 items-center gap-2 rounded-md border border-n-amber-7 px-3 text-xs font-semibold text-n-amber-12 hover:bg-n-amber-4 disabled:opacity-60"
                    :disabled="deletingSimulationId === item.id"
                    @click="removeSuspiciousSimulation(item)"
                  >
                    <span
                      class="size-4"
                      :class="
                        deletingSimulationId === item.id
                          ? 'i-lucide-loader-2 animate-spin'
                          : 'i-lucide-trash-2'
                      "
                    />
                    Excluir
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section
        class="clt-pulse__panel rounded-[1.5rem] border border-n-weak bg-n-solid-1 p-5"
      >
        <div class="mb-3 flex items-center justify-between">
          <h2 class="mb-0 text-base font-semibold text-n-slate-12">
            {{ copy.activity }}
          </h2>
          <span class="text-xs text-n-slate-10">
            {{ recentActivity.length }} eventos
          </span>
        </div>
        <div class="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
          <article
            v-for="item in recentActivity"
            :key="`${item.type}-${item.occurred_at}-${item.title}`"
            class="flex min-w-0 items-center gap-3 rounded-xl border border-n-weak bg-n-alpha-1 p-3.5 transition hover:-translate-y-0.5 hover:bg-n-solid-2"
          >
            <span
              class="grid size-9 shrink-0 place-items-center rounded-lg bg-n-alpha-2 text-n-slate-11"
            >
              <span class="size-4" :class="activityIcon(item.type)" />
            </span>
            <div class="min-w-0 flex-1">
              <div class="truncate text-sm font-medium text-n-slate-12">
                {{ item.title }}
              </div>
              <div class="text-xs text-n-slate-10">
                <CpfReveal
                  :key="`${item.source}-${item.record_id}`"
                  :item="item"
                  :period="periodParams()"
                />
                · {{ formatDate(item.occurred_at) }}
              </div>
              <button
                v-if="item.conversation_id"
                type="button"
                class="text-xs text-n-blue-11"
                @click="radarFilter = { item }"
              >
                Ver conversa #{{ item.conversation_id }}
              </button>
              <span v-else class="text-xs text-n-slate-10" role="status">{{
                'Sem conversa vinculada'
              }}</span>
            </div>
            <span class="shrink-0 text-sm font-semibold text-n-slate-12">
              {{ formatCurrency(item.amount) }}
            </span>
          </article>
        </div>
      </section>
    </section>
    <RadarModal
      v-if="radarFilter"
      :filter="radarFilter"
      :period="periodParams()"
      @close="radarFilter = null"
    />
  </main>
</template>

<style scoped>
.clt-pulse__hero {
  background: radial-gradient(
      circle at 82% 8%,
      rgb(56 153 255 / 20%),
      transparent 32%
    ),
    linear-gradient(135deg, #071a33 0%, #0b3b70 52%, #0b2545 100%);
}

.clt-pulse__content {
  background-color: rgb(var(--background-color));
  background-image: radial-gradient(
    rgb(11 59 112 / 8%) 0.7px,
    transparent 0.7px
  );
  background-size: 22px 22px;
}

.clt-pulse__orbit {
  will-change: transform;
}

.clt-pulse__orbit--outer {
  animation: clt-pulse-orbit 28s linear infinite;
}

.clt-pulse__orbit--inner {
  animation: clt-pulse-orbit 19s linear infinite reverse;
}

.clt-pulse__orbit-dot {
  position: absolute;
  top: -0.3rem;
  left: 50%;
  width: 0.55rem;
  height: 0.55rem;
  border: 1px solid rgb(255 255 255 / 60%);
  border-radius: 9999px;
  background: #3899ff;
  box-shadow:
    0 0 0 5px rgb(56 153 255 / 12%),
    0 0 22px rgb(56 153 255 / 85%);
  animation: clt-pulse-orbit-glow 2.8s ease-in-out infinite;
}

.clt-pulse__orbit--inner .clt-pulse__orbit-dot {
  animation-delay: -1.4s;
}

@keyframes clt-pulse-orbit {
  to {
    transform: rotate(360deg);
  }
}

@keyframes clt-pulse-orbit-glow {
  50% {
    opacity: 0.55;
    box-shadow:
      0 0 0 9px rgb(56 153 255 / 6%),
      0 0 14px rgb(56 153 255 / 55%);
  }
}

:deep(.clt-pulse__date-picker > div > .inline-flex > button:first-child) {
  height: 2.5rem;
  border: 1px solid rgb(255 255 255 / 16%);
  background: rgb(255 255 255 / 12%);
  color: white;
}

:deep(.clt-pulse__date-picker > div > .inline-flex > button:first-child:hover) {
  background: rgb(255 255 255 / 18%);
}

:deep(.clt-pulse__date-picker > div > .inline-flex > button:first-child > *) {
  color: rgb(255 255 255 / 88%) !important;
}

:deep(
    .clt-pulse__date-picker
      > div
      > .inline-flex
      > button:first-child
      > span:nth-of-type(2)
  ) {
  color: rgb(255 255 255 / 68%) !important;
}

.clt-pulse__metric,
.clt-pulse__panel {
  box-shadow:
    0 1px 2px rgb(15 23 20 / 3%),
    0 18px 50px rgb(15 23 20 / 5%);
}

.clt-pulse__metric:hover {
  box-shadow: 0 20px 55px rgb(15 23 20 / 10%);
  transform: translateY(-2px);
}

@media (prefers-reduced-motion: reduce) {
  .clt-pulse__orbit,
  .clt-pulse__orbit-dot,
  .clt-pulse__metric {
    animation: none;
  }

  .clt-pulse__metric {
    transition: none;
  }

  .clt-pulse__metric:hover {
    transform: none;
  }
}
</style>
